import { makeAutoObservable } from "mobx";
import { ExtendedShareholderDTO } from "../../../types/Shareholder";
import { parseInt, sortBy } from "lodash";
import { newUuid } from "../../../utils/uuid";
import { ShareholdingPerDateDTO } from "../../../types/Transactions";
import { combineDateAndTime, toDate } from "../../../utils/Dates";
import { isBlank } from "../../../utils/strings";

import { TMessage } from "../../../i18n/tt";
import {
  LocalDateFieldModel,
  NumberFieldModel,
  StringFieldModel,
  TimeFieldModel,
} from "../../../common/commonForms";
import { calculateTotalNumberOfShares, SharenumberInterval } from "../../shareholderRegisterDomain";
import { ShareholderIdShares } from "../../../types/excelImportDomain";
import { isNorwegianOrgNo } from "../../../utils/orgNo";
import { ShareType } from "../../../common/share";
import { toShareholderReference } from "../../../utils/UrnReference";

export class SharesPerShareTypeModel {
  shareType: ShareType;
  numberOfShares: NumberFieldModel;

  constructor(shareType: ShareType, setDirty: () => void, numberOfShares?: number) {
    this.shareType = shareType;
    this.numberOfShares = new NumberFieldModel({
      onChange: setDirty,
      value: numberOfShares,
      isPositiveOrZero: true,
    });
  }
}

function splitSharenumbers(value: string) {
  return value
    .split(",")
    .filter((s) => !isBlank(s))
    .map((s) => s.trim());
}

export function validateSharenumberIntervals(value: string): TMessage | undefined {
  if (isBlank(value)) {
    return undefined;
  }
  const sharenumberIntervals = splitSharenumbers(value);
  if (sharenumberIntervals.some((s) => !/^\d+-\d+$/.test(s))) {
    return { key: "transactions.shareholdingPerDate.sharenumberIntervals.invalid" };
  }
  return undefined;
}

export function parseSharenumbers(input: string): SharenumberInterval[] {
  return splitSharenumbers(input).map((s) => {
    const [from, to] = s.split("-");
    return {
      sharenumberFromInclusive: parseInt(from),
      sharenumberToInclusive: parseInt(to),
    };
  });
}

export class SharesPerShareholderModel {
  id = newUuid();
  shareType: ShareType;
  shareholder: ExtendedShareholderDTO;
  entryDate: LocalDateFieldModel;
  numberOfShares: NumberFieldModel;
  sharenumberIntervals: StringFieldModel;

  constructor(
    shareType: ShareType,
    shareholder: ExtendedShareholderDTO,
    setDirty: () => void,
    numberOfShares?: number,
    shareNumbers?: string,
    entryDate?: string,
  ) {
    this.shareType = shareType;
    this.shareholder = shareholder;
    this.entryDate = new LocalDateFieldModel({ onChange: setDirty, value: entryDate });
    this.numberOfShares = new NumberFieldModel({
      onChange: setDirty,
      value: numberOfShares,
      isPositiveOrZero: true,
    });
    this.sharenumberIntervals = new StringFieldModel({
      onChange: setDirty,
      validator: (value) => this.validateSharenumbers(value),
      value: shareNumbers,
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  private validateSharenumbers(value: string): TMessage | undefined {
    const error = validateSharenumberIntervals(value);
    if (error) {
      return error;
    }
    const sharenumberIntervals = parseSharenumbers(value);
    const numberOfShares = this.numberOfShares.toNumberOrZero();
    const sumOfIntervals = calculateTotalNumberOfShares(sharenumberIntervals);
    if (sumOfIntervals !== numberOfShares) {
      return { key: "transactions.shareholdingPerDate.sharenumberIntervals.sumMismatch" };
    }
  }
}

function createSharesPerShareholderModel(
  shareType: ShareType,
  shareholder: ExtendedShareholderDTO,
  setDirty: () => void,
  shareholderIdShares?: ShareholderIdShares[],
  entryDateByShareholderId?: Map<number, string | undefined>,
) {
  const shareholderData = shareholderIdShares?.find((s) => s.shareholderId === shareholder.id);
  const shares = shareholderData?.shares?.find((s) => s.shareTypeId === shareType.sharetypeId);
  return new SharesPerShareholderModel(
    shareType,
    shareholder,
    setDirty,
    shares?.numberOfShares,
    shares?.shareNumbers,
    entryDateByShareholderId?.get(shareholder.id),
  );
}

function createSharetypeModel(
  shareType: ShareType,
  sharesPerShareHolders: SharesPerShareholderModel[],
  setDirty: () => void,
) {
  const numberOfShares = sharesPerShareHolders
    .filter((s) => s.shareType.sharetypeId === shareType.sharetypeId)
    .map((s) => s.numberOfShares.toNumberOrZero())
    .reduce((x, y) => x + y, 0);
  return new SharesPerShareTypeModel(
    shareType,
    setDirty,
    numberOfShares === 0 ? undefined : numberOfShares,
  );
}

export class ShareholdingPerDateModel {
  lastTransactionDate: string;
  nominalValue: NumberFieldModel;
  sharesPerShareTypes: SharesPerShareTypeModel[];
  sharesPerShareHolders: SharesPerShareholderModel[];
  date: LocalDateFieldModel;
  time: TimeFieldModel;
  error?: TMessage;
  isDirty = false;
  shareholdingPerDateUnsupportedForARError?: TMessage;
  shareTypes: ShareType[];
  onRefresh: () => void;
  enterShareNumbers = false;

  constructor(
    readonly orgNo: string,
    readonly shareholders: ExtendedShareholderDTO[],
    shareTypes: ShareType[],
    lastTransactionDate: string,
    onRefresh: () => void,
    readonly entryDateByShareholderId?: Map<number, string | undefined>,
    readonly shareholderIdShares?: ShareholderIdShares[],
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.nominalValue = new NumberFieldModel({
      required: true,
      onChange: this.setDirty,
      isPositiveOrZero: true,
    });
    this.sharesPerShareHolders = sortBy(shareholders, "name").flatMap((shareholder) => {
      return shareTypes.map((shareType) =>
        createSharesPerShareholderModel(
          shareType,
          shareholder,
          this.setDirty,
          shareholderIdShares,
          entryDateByShareholderId,
        ),
      );
    });
    this.enterShareNumbers =
      shareholderIdShares?.some((s) => s.shares?.some((ss) => ss.shareNumbers)) ?? false;

    this.sharesPerShareTypes = shareTypes.map((shareType) =>
      createSharetypeModel(shareType, this.sharesPerShareHolders, this.setDirty),
    );
    this.date = new LocalDateFieldModel({
      required: true,
      minDate: lastTransactionDate,
      onChange: this.setDirty,
    });
    this.time = new TimeFieldModel({ required: true, onChange: this.setDirty });
    this.lastTransactionDate = lastTransactionDate;
    this.shareTypes = shareTypes;
    this.onRefresh = onRefresh;
  }

  onNewShareTypeAdded(shareType: ShareType) {
    this.sharesPerShareTypes.push(
      createSharetypeModel(shareType, this.sharesPerShareHolders, this.setDirty),
    );

    this.shareholders.forEach((shareholder) => {
      this.sharesPerShareHolders.push(
        createSharesPerShareholderModel(
          shareType,
          shareholder,
          this.setDirty,
          this.shareholderIdShares,
          this.entryDateByShareholderId,
        ),
      );
    });
    this.sharesPerShareHolders = sortBy(this.sharesPerShareHolders, (s) => s.shareholder.id);
  }

  addNewShareholder(shareholder: ExtendedShareholderDTO) {
    this.shareholders.push(shareholder);
    this.sharesPerShareHolders = [
      ...this.sharesPerShareHolders,
      ...this.shareTypes.map(
        (shareType) => new SharesPerShareholderModel(shareType, shareholder, this.setDirty),
      ),
    ];
  }

  setDirty() {
    this.isDirty = true;
  }

  get numberOfShareholders() {
    return this.sharesPerShareHolders.length / this.shareTypes.length;
  }

  get hasShareholders(): boolean {
    return this.sharesPerShareHolders.length > 0;
  }

  get activeShareTypeIds() {
    const shareTypesWithValues = this.sharesPerShareTypes
      .filter((s) => !s.numberOfShares.isEmpty)
      .map((s) => s.shareType.sharetypeId);
    return shareTypesWithValues.length > 0
      ? shareTypesWithValues
      : [this.sharesPerShareTypes[0].shareType.sharetypeId];
  }

  get isValid(): boolean {
    return !this.error && this.date.isValid && this.time.isValid && this.nominalValue.isValid;
  }

  validate(): boolean {
    this.error = undefined;
    this.date.validate();
    this.time.validate();
    this.nominalValue.validate();

    this.sharesPerShareTypes.forEach((s) => s.numberOfShares.validate());
    this.sharesPerShareHolders.forEach((s) => s.numberOfShares.validate());
    if (this.sharesPerShareHolders.some((s) => !s.entryDate.validate())) {
      this.error = { key: "transactions.shareholdingPerDate.invalidEntryDate" };
    }

    if (!this.sharesPerShareHolders.some((s) => s.numberOfShares.toNumberOrZero() > 0)) {
      this.error = { key: "transactions.shareholdingPerDate.missingSharesPerShareholderError" };
    }
    if (!this.sharesPerShareTypes.some((s) => s.numberOfShares.toNumberOrZero() > 0)) {
      this.error = { key: "transactions.shareholdingPerDate.missingSharesPerShareTypeError" };
    }
    if (!this.error) {
      if (this.hasShareSumMismatch()) {
        this.error = { key: "transactions.shareholdingPerDate.shareSumError" };
      }
    }
    if (!this.error && this.enterShareNumbers) {
      if (this.isMissingSharenumbers()) {
        this.error = { key: "transactions.shareholdingPerDate.sharenumberIntervals.missing" };
      } else if (this.sharesPerShareHolders.some((s) => !s.sharenumberIntervals.validate())) {
        this.error = { key: "transactions.shareholdingPerDate.sharenumberIntervals.sumMismatch" };
      }
    }

    return this.isValid;
  }

  hasShareSumMismatch() {
    return this.sharesPerShareTypes
      .filter((s) => !s.numberOfShares.isEmpty)
      .some(
        (sharesPerShareType) =>
          sharesPerShareType.numberOfShares.toNumberOrZero() !==
          this.getAllocatedShares(sharesPerShareType),
      );
  }

  isMissingSharenumbers() {
    return this.sharesPerShareHolders.some(
      (s) => s.numberOfShares.toNumberOrZero() > 0 && s.sharenumberIntervals.isEmpty,
    );
  }

  private getAllocatedShares(sharesPerShareType: SharesPerShareTypeModel) {
    return this.sharesPerShareHolders
      .filter((s) => s.shareType.sharetypeId === sharesPerShareType.shareType.sharetypeId)
      .map((s) => s.numberOfShares.toNumberOrZero())
      .reduce((previous, current) => previous + current);
  }

  prepareData(): ShareholdingPerDateDTO["data"] {
    return {
      organizationnumber: this.orgNo,
      shareAllocation: this.sharesPerShareHolders
        .map((s) => ({
          shareholderId: s.shareholder.id,
          sharetypeId: s.shareType.sharetypeId,
          numberOfShares: s.numberOfShares.parsedValue ?? 0,
          sharenumberIntervals: parseSharenumbers(s.sharenumberIntervals.validatedValue),
          entryDate: s.entryDate.validatedValue
            ? combineDateAndTime(s.entryDate.validatedValue, "12:00")
            : undefined,
        }))
        .filter((s) => s.numberOfShares > 0),
      transactionDate: combineDateAndTime(
        this.date.validatedValue ?? "",
        this.time.validatedValue ?? "",
      ),
      nominalValue: this.nominalValue.toNumberOrZero(),
    };
  }

  checkReportingYearFor() {
    if (this.date.validatedValue) {
      const currentYear = new Date().getFullYear();
      const transactionYear = toDate(this.date.validatedValue).getFullYear();
      if (transactionYear === currentYear && isNorwegianOrgNo(this.orgNo)) {
        this.shareholdingPerDateUnsupportedForARError = {
          key: "transactions.shareholdingPerDate.transactionDate.info",
          params: { year: currentYear.toString(), latest: `31.12.${currentYear - 1}` },
        };
      } else {
        this.shareholdingPerDateUnsupportedForARError = undefined;
      }
    }
  }

  toggleEnterShareNumbers() {
    this.enterShareNumbers = !this.enterShareNumbers;
  }

  get excludedShareholders() {
    return this.shareholders.map(
      (shareholder) =>
        (shareholder.isCompany
          ? shareholder.companyNumber
          : shareholder.personInfo?.socialSecurityNumber) ?? toShareholderReference(shareholder.id),
    );
  }

  addNewSubscribers(shareholders: ExtendedShareholderDTO[], onSuccess: () => void) {
    shareholders.map((shareholder) => this.addNewShareholder(shareholder));
    onSuccess();
  }

  clearDirty() {
    this.isDirty = false;
  }
}
